import React from 'react';
import {
  Box,
  Grid,
  GridItem,
  Text,
  Spinner,
  Input,
  Button,
  Select,
  useToast,
} from '@chakra-ui/react';
import useSWR from 'swr';
import xhr from 'xhr';
import { LabelForm, FormColumns } from 'components/wrapper';
import useAuth from 'hooks/useAuth';
import { diffFields, logStudentChanges } from './audit';
import MunicipalityInput from './MunicipalityInput';
import { sanitizeDocumentNumber } from 'utils/documentNumber';

const normalizeParentesco = (value) => {
  const rel = String(value || '').trim().toLowerCase();

  if (!rel) return '';
  if (rel === 'madre' || rel === 'mamá' || rel === 'mama') return 'madre';
  if (rel === 'padre' || rel === 'papá' || rel === 'papa') return 'padre';
  if (rel === 'otro' || rel === 'otros') return 'otro';

  return rel;
};

const fetchParents = async ({ studentId }) => {
  const res = await xhr(`/parents?filters[students][id][$eq]=${studentId}&populate=*`);
  return res?.data?.data || [];
};

const fetchIdentityTypes = async () => {
  const res = await xhr('/identity-types?pagination[pageSize]=100');
  return res?.data?.data || [];
};

const buildParentForm = (parent) => ({
  id: parent?.id || null,
  full_name: parent?.full_name || '',
  email: parent?.email || '',
  home_phone: parent?.home_phone || '',
  identity: parent?.identity || '',
  identity_type: parent?.identity_type?.id ? String(parent.identity_type.id) : '',
  expedition_place: parent?.expedition_place || '',
  state: parent?.state || '',
  city: parent?.city || '',
  address: parent?.address || '',
  parentesco: parent?.parentesco || '',
});

const ParentCard = ({ title, parent, identityTypes, mutate, studentId }) => {
  const toast = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(false);

  const [form, setForm] = React.useState({
    id: null,
    full_name: '',
    email: '',
    home_phone: '',
    identity: '',
    identity_type: '',
    expedition_place: '',
    state: '',
    city: '',
    address: '',
    parentesco: '',
  });

  React.useEffect(() => {
    setForm(buildParentForm(parent));
  }, [parent]);

  const typeNameOf = (id) => identityTypes?.find((el) => String(el.id) === String(id))?.name;

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdate = async () => {
    if (loading) return;
    const isCreating = !form.id;
    try {
      if (isCreating && !form.full_name.trim()) {
        toast({
          description: 'Ingresa el nombre completo del acudiente para crearlo.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (form.expedition_place !== (parent?.expedition_place || '') && /\d/.test(form.expedition_place)) {
        toast({
          description: 'El lugar de expedición no puede llevar fechas ni números.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setLoading(true);

      const baseData = {
        full_name: form.full_name,
        email: form.email,
        home_phone: form.home_phone,
        identity: form.identity,
        expedition_place: form.expedition_place,
        state: form.state,
        city: form.city,
        address: form.address,
      };

      if (isCreating) {
        const response = await xhr.post('/parents', {
          data: {
            ...baseData,
            full_name: form.full_name.trim(),
            identity_type: form.identity_type ? Number(form.identity_type) : null,
            parentesco: title,
            students: [Number(studentId)],
          },
        });
        // Guardar el ID evita crear de nuevo si falla la recarga del listado.
        const createdId = response?.data?.data?.id;
        if (createdId) setForm((prev) => ({ ...prev, id: createdId }));
      } else {
        try {
          await xhr.put(`/parents/${form.id}`, {
            data: {
              ...baseData,
              identity_type: form.identity_type ? Number(form.identity_type) : null,
            },
          });
        } catch (firstError) {
          await xhr.put(`/parents/${form.id}`, {
            data: {
              ...baseData,
              identity_type: form.identity_type
                ? { connect: [Number(form.identity_type)] }
                : null,
            },
          });
        }
      }
      const typeName = (id) => identityTypes.find((el) => String(el.id) === String(id))?.name || id;
      const before = buildParentForm(parent);
      await logStudentChanges({
        studentId,
        userId: user.id,
        section: isCreating ? 'creación de acudiente' : 'acudientes',
        changes: diffFields(
          { ...before, identity_type: typeName(before.identity_type) },
          { ...form, identity_type: typeName(form.identity_type) },
          {
            full_name: 'Nombre completo',
            email: 'Correo',
            home_phone: 'Teléfono',
            identity_type: 'Tipo de documento',
            identity: 'Número de documento',
            expedition_place: 'Lugar de expedición',
            state: 'Departamento',
            city: 'Ciudad',
            address: 'Dirección',
          }
        ).map((change) => ({ ...change, label: `${title}: ${change.label}` })),
      });

      toast({
        description: `${title} ${isCreating ? 'creado' : 'actualizado'} correctamente`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      mutate();
    } catch (error) {
      console.error(error);
      toast({
        description: `Error al ${isCreating ? 'crear' : 'actualizar'} ${title.toLowerCase()}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box marginY="11px">
      <Text fontSize="23px" fontWeight="500" marginBottom="20px">
        {title}
      </Text>

      <Box
        boxShadow="0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 #574feb"
        borderRadius="5px"
        bg="white"
      >
        <FormColumns borderBottom="1px solid #e6e6eb" paddingBottom="10px" marginBottom="10px">
          <Grid templateColumns="repeat(4, 1fr)" alignItems="center">
            <GridItem colSpan={1} paddingTop="10px" paddingLeft="20px">
              <LabelForm>Nombre completo</LabelForm>
            </GridItem>
            <GridItem colSpan={3} paddingTop="10px" paddingRight="20px">
              <Input
                placeholder="Nombre completo"
                value={form.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
              />
            </GridItem>
          </Grid>
        </FormColumns>

        <FormColumns borderBottom="1px solid #e6e6eb" paddingBottom="10px" marginBottom="10px">
          <Grid templateColumns="repeat(4, 1fr)" alignItems="center">
            <GridItem colSpan={1} paddingTop="10px" paddingLeft="20px">
              <LabelForm>Correo</LabelForm>
            </GridItem>
            <GridItem colSpan={3} paddingTop="10px" paddingRight="20px">
              <Input
                placeholder="Correo"
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </GridItem>
          </Grid>
        </FormColumns>

        <FormColumns borderBottom="1px solid #e6e6eb" paddingBottom="10px" marginBottom="10px">
          <Grid templateColumns="repeat(4, 1fr)" alignItems="center">
            <GridItem colSpan={1} paddingTop="10px" paddingLeft="20px">
              <LabelForm>Teléfono</LabelForm>
            </GridItem>
            <GridItem colSpan={3} paddingTop="10px" paddingRight="20px">
              <Input
                placeholder="Teléfono"
                value={form.home_phone}
                onChange={(e) => handleChange('home_phone', e.target.value)}
              />
            </GridItem>
          </Grid>
        </FormColumns>

        <FormColumns borderBottom="1px solid #e6e6eb" paddingBottom="10px" marginBottom="10px">
          <Grid templateColumns="repeat(4, 1fr)" alignItems="center">
            <GridItem colSpan={1} paddingTop="10px" paddingLeft="20px">
              <LabelForm>Tipo de documento</LabelForm>
            </GridItem>
            <GridItem colSpan={3} paddingTop="10px" paddingRight="20px">
              <Select
                placeholder="Seleccione tipo de documento"
                value={form.identity_type}
                onChange={(e) => {
                  const typeId = e.target.value;
                  handleChange('identity_type', typeId);
                  // Al cambiar el tipo, el número se ajusta a la regla del nuevo tipo.
                  if (form.identity && form.identity !== (parent?.identity || '')) {
                    handleChange('identity', sanitizeDocumentNumber(form.identity, typeNameOf(typeId)));
                  }
                }}
              >
                {identityTypes.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </GridItem>
          </Grid>
        </FormColumns>

        <FormColumns borderBottom="1px solid #e6e6eb" paddingBottom="10px" marginBottom="10px">
          <Grid templateColumns="repeat(4, 1fr)" alignItems="center">
            <GridItem colSpan={1} paddingTop="10px" paddingLeft="20px">
              <LabelForm>Número de documento</LabelForm>
            </GridItem>
            <GridItem colSpan={3} paddingTop="10px" paddingRight="20px">
              <Input
                placeholder="Número de documento"
                value={form.identity}
                onChange={(e) => handleChange('identity', sanitizeDocumentNumber(e.target.value, typeNameOf(form.identity_type)))}
              />
            </GridItem>
          </Grid>
        </FormColumns>

        <FormColumns borderBottom="1px solid #e6e6eb" paddingBottom="10px" marginBottom="10px">
          <Grid templateColumns="repeat(4, 1fr)" alignItems="center">
            <GridItem colSpan={1} paddingTop="10px" paddingLeft="20px">
              <LabelForm>Lugar de expedición</LabelForm>
            </GridItem>
            <GridItem colSpan={3} paddingTop="10px" paddingRight="20px">
              <MunicipalityInput
                value={form.expedition_place}
                initialValue={parent?.expedition_place || ''}
                onChange={(place) => handleChange('expedition_place', place)}
              />
            </GridItem>
          </Grid>
        </FormColumns>

        <FormColumns borderBottom="1px solid #e6e6eb" paddingBottom="10px" marginBottom="10px">
          <Grid templateColumns="repeat(4, 1fr)" alignItems="center">
            <GridItem colSpan={1} paddingTop="10px" paddingLeft="20px">
              <LabelForm>Departamento</LabelForm>
            </GridItem>
            <GridItem colSpan={3} paddingTop="10px" paddingRight="20px">
              <Input
                placeholder="Departamento"
                value={form.state}
                onChange={(e) => handleChange('state', e.target.value)}
              />
            </GridItem>
          </Grid>
        </FormColumns>

        <FormColumns borderBottom="1px solid #e6e6eb" paddingBottom="10px" marginBottom="10px">
          <Grid templateColumns="repeat(4, 1fr)" alignItems="center">
            <GridItem colSpan={1} paddingTop="10px" paddingLeft="20px">
              <LabelForm>Ciudad</LabelForm>
            </GridItem>
            <GridItem colSpan={3} paddingTop="10px" paddingRight="20px">
              <Input
                placeholder="Ciudad"
                value={form.city}
                onChange={(e) => handleChange('city', e.target.value)}
              />
            </GridItem>
          </Grid>
        </FormColumns>

        <FormColumns paddingBottom="10px">
          <Grid templateColumns="repeat(4, 1fr)" alignItems="center">
            <GridItem colSpan={1} paddingTop="10px" paddingLeft="20px">
              <LabelForm>Dirección</LabelForm>
            </GridItem>
            <GridItem colSpan={3} paddingTop="10px" paddingRight="20px">
              <Input
                placeholder="Dirección"
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
              />
            </GridItem>
          </Grid>
        </FormColumns>
      </Box>

      <Button
        onClick={handleUpdate}
        colorScheme="purple"
        marginY="15px"
        width="170px"
        isLoading={loading}
      >
        {form.id ? 'Actualizar' : 'Crear'} {title}
      </Button>
    </Box>
  );
};

const ParentsInfo = ({ student }) => {
  const {
    data: parentsData,
    error: parentsError,
    mutate,
  } = useSWR(
    student?.id ? { studentId: student.id, type: 'parents-info' } : null,
    fetchParents,
    { revalidateOnFocus: false }
  );

  const {
    data: identityTypesData,
    error: identityTypesError,
  } = useSWR('identity-types', fetchIdentityTypes, {
    revalidateOnFocus: false,
  });

  if ((!parentsData && !parentsError) || (!identityTypesData && !identityTypesError)) {
    return <Spinner />;
  }

  if (parentsError) {
    return <Text color="red.500">No se pudieron cargar los acudientes. Recarga la página para intentarlo de nuevo.</Text>;
  }

  const parents = Array.isArray(parentsData) ? parentsData : [];
  const identityTypes = Array.isArray(identityTypesData) ? identityTypesData : [];

  let madre = null;
  let padre = null;
  let otro = null;
  let sinParentesco = null;

  parents.forEach((parent) => {
    const rel = normalizeParentesco(parent?.parentesco);

    if (rel === 'madre' && !madre) madre = parent;
    else if (rel === 'padre' && !padre) padre = parent;
    else if (rel === 'otro' && !otro) otro = parent;
    else if (!rel && !sinParentesco) sinParentesco = parent;
  });

  if (!otro && sinParentesco) {
    otro = sinParentesco;
  }

  return (
    <Box>
      <ParentCard title="Madre" parent={madre} identityTypes={identityTypes} mutate={mutate} studentId={student.id} />
      <ParentCard title="Padre" parent={padre} identityTypes={identityTypes} mutate={mutate} studentId={student.id} />
      <ParentCard title="Otro" parent={otro} identityTypes={identityTypes} mutate={mutate} studentId={student.id} />
    </Box>
  );
};

export default ParentsInfo;